import React, { useEffect, useState } from "react";
import axios from "axios";
import { AvatarLabelGroup, Badge, Pagination, SearchLg, Table, Tag, TextInput } from "@opendate-app/setlist";

import { useBroadcastEditContext } from "../BroadcastEditContainer";
import { formatSenderNumber } from "../Composer/smsMessage";
import { toastError } from "../../../../shared/toastHelper";
import { formatCount } from "./reportMath";
import { countOrUndefined } from "../../../shared/components/CountBadge";

const EMAIL_FILTERS = [
  { value: "all", label: "All" },
  { value: "opened", label: "Opened" },
  { value: "clicked", label: "Clicked" },
  { value: "not_opened", label: "Didn't open" },
  { value: "bounced", label: "Bounced" },
  { value: "unsubscribed", label: "Unsubscribed" },
];

const SMS_FILTERS = [
  { value: "all", label: "All" },
  { value: "clicked", label: "Clicked" },
  { value: "delivered", label: "No action" },
  { value: "failed", label: "Failed" },
  { value: "opted_out", label: "Opted out" },
];

const STATUS_BADGES = {
  delivered: { label: "Delivered", color: "gray" },
  opened: { label: "Opened", color: "brand" },
  clicked: { label: "Clicked", color: "success" },
  bounced: { label: "Bounced", color: "warning" },
  suppressed: { label: "Suppressed", color: "warning" },
  unsubscribed: { label: "Unsubscribed", color: "error" },
  failed: { label: "Failed", color: "error" },
  opted_out: { label: "Opted out", color: "warning" },
  pending: { label: "Sending", color: "gray" },
};

const initials = (name, email) => (name || email || "?")
  .split(/[\s@.]+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0].toUpperCase())
  .join("");

const ReportRecipients = () => {
  const { csrfToken, team, broadcast, formatDate, isEmailTemplate } = useBroadcastEditContext();
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  const formatTime = (value) => (value ? formatDate(value) : "—");
  const filters = isEmailTemplate ? EMAIL_FILTERS : SMS_FILTERS;
  const contact = (row) => (isEmailTemplate ? row.email : row.phone && formatSenderNumber(row.phone));

  useEffect(() => {
    let current = true;
    setLoading(true);
    const debounce = setTimeout(() => {
      setFailed(false);
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      axios.get(`/teams/${team.id}/broadcasts/${broadcast.id}/recipients.json`, { params: { filter, q: query, page } })
        .then(({ data }) => {
          if (!current) return;
          setResult(data);
          setLoading(false);
        })
        .catch(() => {
          if (!current) return;
          setFailed(true);
          setLoading(false);
          toastError("Could not load recipients, please try again");
        });
    }, query ? 300 : 0);

    return () => {
      current = false;
      clearTimeout(debounce);
    };
  }, [filter, query, page]);

  const pickFilter = (value) => {
    setFilter(value);
    setPage(1);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {filters.map((option) => (
          <Tag
            key={option.value}
            selectable
            selected={filter === option.value}
            onSelectedChange={() => pickFilter(option.value)}
            count={result && countOrUndefined(result.counts[option.value]) ? formatCount(result.counts[option.value]) : undefined}
          >
            {option.label}
          </Tag>
        ))}
        <span style={{ flex: 1 }} />
        <div style={{ width: 280 }}>
          <TextInput
            size="sm"
            aria-label="Search fans"
            placeholder="Search fans"
            leadingIcon={SearchLg}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <Table
        loading={loading}
        loadingLabel="Loading recipients"
        error={failed ? "Couldn't load recipients." : null}
        getRowKey={(row) => row.id}
        data={result?.recipients || []}
        empty={<div style={{ fontSize: 14, color: "var(--sl-text-tertiary)" }}>No fans match.</div>}
        columns={[
          {
            key: "fan",
            header: "Name",
            primary: true,
            render: (row) => (
              <AvatarLabelGroup
                size="sm"
                alt={row.name || contact(row)}
                name={row.fan_path ? <a href={row.fan_path} style={{ color: "var(--sl-text-brand)" }}>{row.name || contact(row)}</a> : row.name || contact(row)}
                supportingText={row.name ? contact(row) : null}
                initials={initials(row.name, contact(row))}
              />
            ),
          },
          {
            key: "status",
            header: "Status",
            render: (row) => <Badge size="sm" color={STATUS_BADGES[row.status].color}>{STATUS_BADGES[row.status].label}</Badge>,
          },
          ...(isEmailTemplate ? [{ key: "opened_at", header: "Opened", render: (row) => formatTime(row.opened_at) }] : []),
          { key: "clicked_at", header: "Clicked", render: (row) => formatTime(row.clicked_at) },
          { key: "tickets_bought", header: "Tickets bought", align: "right", render: (row) => (row.tickets_bought ? formatCount(row.tickets_bought) : "—") },
        ]}
      />

      {result && result.total_pages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 14, color: "var(--sl-text-tertiary)" }}>
          <span>Showing {formatCount(result.recipients.length)} of {formatCount(result.total)}</span>
          <Pagination currentPage={page} totalPages={result.total_pages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
};

export default ReportRecipients;
